import type { ImmutableArray } from "shelving";
import { useState } from "react";
import { INVITE_MEMBER } from "shared/api";
import { AsyncButton, Button, CardLoader, Icon, List, Loader } from "shared/react";
import { PROFILE_MAX, type ProfileData } from "shared/schema";
import { getKeywordQuery } from "shared/schema";
import { FilterForm } from "../misc/FilterForm.js";
import { ProfileCard } from "../profile/ProfileCard.js";
import { callAPI } from "../util/api.js";
import { useFollowerData, useMemberItem } from "../util/data.js";
import { useQuery } from "../util/db.js";
import { MemberStatusBadge } from "./MemberStatusBadge.js";

export function InviteList({ gid }: { gid: string }): React.ReactElement {
	const [words, setWords] = useState<ImmutableArray<string>>([]);
	const store = useQuery("profile", words.length ? { ...getKeywordQuery<ProfileData>(words, ["!verified", "!followers"]), $limit: PROFILE_MAX } : undefined);
	return (
		<>
			<FilterForm onFilter={setWords} />
			{!store ? null : (
				<Loader>
					<List
						store={store}
						mapper={({ id: uid }) => (
							<CardLoader>
								<ProfileCard key={uid} uid={uid}>
									<footer className="space-none pad-none">
										<InviteButton gid={gid} uid={uid} />
									</footer>
								</ProfileCard>
							</CardLoader>
						)}
						empty="No matching profiles"
					/>
				</Loader>
			)}
		</>
	);
}

/** Show an invite button for a given user (only if they follow you and are not already a member). */
function InviteButton({ gid, uid }: { gid: string; uid: string }): React.ReactElement {
	const member = useMemberItem(gid, uid);
	const follower = useFollowerData(uid);
	return member.exists ? (
		<MemberStatusBadge mods="pin-bottom pin-right" status={member.data.status} />
	) : follower?.status === "approved" ? (
		<AsyncButton mods="button-default" onClick={() => callAPI(INVITE_MEMBER, { gid, uid })}>
			<Icon icon="add" /> <span>Invite</span>
		</AsyncButton>
	) : (
		<Button>
			<Icon icon="close" /> <span>Not following you</span>
		</Button>
	);
}
